import _ from 'lodash-es';

import { sentimentTypeMap } from '@op/components/ReviewsModal/constants';

/**
 * 判断是否需要进行格式化
 * @param value  值
 * @param greaterThanThousandFormat 大于1000是否需要格式化。
 * @param lessThanThousandFormat 小于1000是否需要格式化。
 * @returns
 */
function dealwithThousandValue(
  value: number,
  greaterThanThousandFormat: boolean,
  lessThanThousandFormat: boolean,
) {
  if (value >= 1000 && greaterThanThousandFormat) {
    return true;
  }

  if (value < 1000 && lessThanThousandFormat) {
    return true;
  }

  return false;
}

export const getUnitValue = (value: number): [string, number] => {
  // 获取单位
  let unit = '';
  if (value >= 1000000000) {
    value = value / 1000000000;
    unit = 'B';
  } else if (value >= 1000000) {
    value = value / 1000000;
    unit = 'M';
  } else if (value >= 1000) {
    value = value / 1000;
    unit = 'K';
  }

  return [unit, value];
};

/**
 * 格式化的公共方法
 * @param value     数值
 * @param decimalDigit  保留位数
 * @param defaultValue  如果为空或者0返回默认值
 * @param allFormat     所有的都要格式化
 * @param greaterThanThousandFormat 大于1000是否需要格式化。 allFormat为false生效
 * @param lessThanThousandFormat 小于1000是否需要格式化。  allFormat为false生效
 * @param removeAfterZero  是否去掉小数点后的无效0
 * @returns
 */
function commonFormatValue({
  value,
  decimalDigit = 2,
  defaultValue = '-',
  allFormat = true,
  removeAfterZero = false,
  greaterThanThousandFormat = true,
  lessThanThousandFormat = false,
}: {
  value: number | string;
  decimalDigit?: number;
  defaultValue?: string | number;
  removeAfterZero?: boolean;
  allFormat?: boolean;
  greaterThanThousandFormat?: boolean;
  lessThanThousandFormat?: boolean;
}) {
  if (!value || (typeof value === 'number' && isNaN(value))) {
    return defaultValue;
  }
  // 先转数字
  value = Number(value);
  // 获取正负号
  let burden = value < 0 ? '-' : '';
  // 再取绝对值
  value = Math.abs(value);

  // 判断大余1000是否进行格式化，小于1000是否进行格式化
  let isFormat =
    allFormat ||
    dealwithThousandValue(
      value,
      greaterThanThousandFormat,
      lessThanThousandFormat,
    );

  // 获取单位
  let unit = '';
  [unit, value] = getUnitValue(value);

  // 如果所有数据都要进行保留小数位数，那就进去
  if (allFormat || isFormat) {
    value = value?.toFixed(decimalDigit);
  }
  // 需要去掉小数后位的0
  if (removeAfterZero) {
    value = parseFloat(value + '');
  }
  // 返回组装值
  return burden + value + unit;
}

/**
 * TODO: 其他系统使用
 * 格式化分数
 * @param score
 * @param decimalDigit
 * @returns
 */
export function scoreFormat(
  score: number,
  decimalDigit = 1,
  defaultValue: string | number = '-',
) {
  return commonFormatValue({ value: score, decimalDigit, defaultValue });
}

/**
 * 格式化平均数等问题，大于1000的会格式化，小于1000的也会格式化
 * @param value
 * @param decimalDigit
 * @returns
 */
export function avgValueFormat(value: number, decimalDigit = 2) {
  return commonFormatValue({ value, decimalDigit, allFormat: true });
}

/**
 * TODO: 其他系统使用
 * 格式化整数，小于1000就不格式化了
 * @param value
 * @param decimalDigit
 * @param defaultValue
 * @returns
 */
export function reviewsFormat(
  value: number,
  decimalDigit = 2,
  defaultValue = '-',
) {
  if (value < 1000) {
    return commonFormatValue({
      value,
      allFormat: false,
      lessThanThousandFormat: true,
      decimalDigit,
      defaultValue,
      removeAfterZero: true,
    });
  }
  return commonFormatValue({
    value,
    allFormat: false,
    greaterThanThousandFormat: true,
    decimalDigit,
    defaultValue,
  });
}

/**
 * 格式化整数，小于1000就不格式化了
 * @param value
 * @param decimalDigit
 * @param defaultValue
 * @returns
 */
export function reviewsMaxZeroFormat(
  value: number,
  decimalDigit: number = 2,
  defaultValue = '-',
) {
  if (value < 0) return defaultValue;
  return reviewsFormat(value, decimalDigit, defaultValue);
}

/**
 * TODO: 其他系统使用
 * y轴数据格式化，全部格式化，如果有小数点后有0也会去掉
 * @param value
 * @param decimalDigit
 * @returns
 */
export function yAxleFormat(value: number, decimalDigit = 1) {
  return commonFormatValue({
    value,
    allFormat: true,
    defaultValue: '0',
    decimalDigit,
    removeAfterZero: true,
  });
}

/**
 * eCharts 轴格式化
 * @param value
 * @param index
 * @returns
 */
export function yAxisFormat(value: any) {
  if (value == 0) {
    return 0;
  }
  if (value >= 1000000000) {
    return Math.round(value / 10000000) / 100 + 'B';
  }
  if (value >= 1000000) {
    return Math.round(value / 10000) / 100 + 'M';
  }
  if (value >= 1000) {
    return Math.round(value / 10) / 100 + 'K';
  }
  return Math.round(value * 100) / 100;
}

/**
 * eCharts 小数轴格式化
 * @param value
 * @param index
 * @returns
 */
export function percentYAxisFormat(value: any, isMin: boolean = false) {
  if (value == 0) return 0;
  return `${(value * 100).toFixed(isMin ? 2 : 0)}%`;
}

/**
 * TODO: 其他系统使用
 * 情感分格式化
 * @param value
 * @param defaultValue
 * @returns
 */
export function sentimentFormat(
  value: number | string,
  defaultValue: string | number = '-',
) {
  value = typeof value === 'string' ? Number(value) : value;
  // 向下取整，但是最后保留一位小数是为了添加.0  【3 -> 3.0】
  return !value ? defaultValue : (Math.floor(value * 1000) / 1000).toFixed(2);
}

/**
 * TODO: 其他系统使用
 * 品牌健康值格式化
 * @param value
 * @param defaultValue
 * @returns
 */
export function brandHealthFormat(
  value: number | string,
  defaultValue: string | number = '-',
) {
  if (value === -99999 || value === '-99999') return defaultValue;
  if (typeof value !== 'number' && typeof value !== 'string')
    return defaultValue;
  value = typeof value === 'string' ? Number(value) : value;
  if (isNaN(value)) return defaultValue;
  if (value === 100 || value === -100 || value === 0) return value;
  return Math.round(value);
}

export function trendsReviewsFormat(value: number) {
  return `${commonFormatValue({
    value,
    allFormat: false,
    greaterThanThousandFormat: false,
  })}+`;
}

/**
 * TODO: 其他系统使用
 * 占比格式化
 * @param percentage
 * @param defaultValue
 * @param decimalDigit
 * @returns
 */
export const percentageFormat = (
  percentage: number,
  defaultValue = '-',
  decimalDigit = 2,
) => {
  if (!percentage) {
    return defaultValue;
  }
  if (percentage === 100) {
    return '100%';
  }
  const value = Math.abs(percentage);
  if (value > 9999) {
    return '9999+%';
  }
  return `${value.toFixed(decimalDigit)}%`;
};

/**
 * 占比格式化, 支持正负数
 * @param percentage
 * @param defaultValue
 * @param decimalDigit
 * @returns
 */
export function percentageFormatRpo(
  percentage: number,
  defaultValue = '-',
  decimalDigit = 2,
) {
  if (!percentage) {
    return defaultValue;
  }
  const isMin = percentage < 0;
  const value = percentageFormat(
    Math.abs(percentage),
    defaultValue,
    decimalDigit,
  );

  return `${isMin ? '-' : ''}${value}`;
}

/**
 * 小数占比格式化
 * @param percentage
 * @param defaultValue
 * @param decimalDigit
 * @returns
 */
export const decimalFormatPercentage = (
  decimal: number,
  defaultValue: string = '-',
  decimalDigit: number = 2,
) => {
  const value = decimal * 100;
  if (isNaN(value)) return defaultValue;
  return percentageFormat(value, defaultValue, decimalDigit);
};

/**
 * 小数占比小于0.01%的时候展示< 0.01%格式化
 * @param decimal：需要转换成百分比的小数。
 * @param defaultValue：当转换失败或者满足特定条件时返回的默认字符串。
 * @param decimalDigit：转换后的百分比字符串中小数点后的位数。
 * @param minValue：一个阈值，当输入的小数小于这个值时，会返回 minValueText。
 * @param minValueText：当输入的小数小于 minValue 时返回的特定文本。// 正确的拼写应该是 Decimal
 * @returns
 */
export function specialSecimalFormatPercentage(
  decimal: number,
  defaultValue = '-',
  decimalDigit = 2,
  minValue = 0.0001,
  minValueText = '< 0.01%',
) {
  const value = decimal * 100;
  if (isNaN(value)) return defaultValue;
  if (decimal > 0 && decimal < minValue) return minValueText;
  return percentageFormatRpo(value, defaultValue, decimalDigit);
}

export function specialSecimalFormatPercentagePro(
  decimal: number,
  defaultValue = '-',
  formatList: Array<{
    decimalDigit: number;
    minValue: number;
    minValueText: string;
  }> = [
    { decimalDigit: 2, minValue: 0.0001, minValueText: '< 0.01%' },
    { decimalDigit: 4, minValue: 0.000001, minValueText: '< 0.01%' },
  ],
): string {
  for (let i = 0; i < formatList.length; i++) {
    const { decimalDigit, minValue, minValueText } = formatList[i];
    const value = specialSecimalFormatPercentage(
      decimal,
      defaultValue,
      decimalDigit,
      minValue,
      minValueText,
    );

    if (value !== minValueText) return value;

    if (i === formatList.length - 1) {
      return value;
    }
  }

  return defaultValue;
}

/**
 * TODO: 其他系统使用
 * 数据转化： 1000 =》 1,000
 * @param value
 * @returns
 */
export const formatThousandthValue = (value: number) => {
  return value?.toLocaleString();
};

/**
 * 数组元素交换
 * @param arr    原数组
 * @param index1 需要交换的下标
 * @param index2 需要交换的另一个下标
 */
export function swapArray(arr: Array<any>, index1: number, index2: number) {
  if (Array.isArray(arr)) {
    [arr[index1], arr[index2]] = [arr[index2], arr[index1]];
  }
}

/**
 * 获取百分比
 * @param value
 * @param total
 * @returns
 */
export function getPercentage(value: number, total: number, digit: number = 2) {
  if (total === 0 || typeof value !== 'number' || typeof total !== 'number') {
    return '0%';
  }
  return `${Number(((value / total) * 100).toFixed(digit))}%`;
}

/**
 * 获取单位
 * @param value
 * @returns
 */
export function getRadix(value: number) {
  if (value >= 1000000000) {
    return 1000000000;
  }
  if (value >= 1000000) {
    return 1000000;
  }
  if (value >= 1000) {
    return 1000;
  }

  if (value >= 100) {
    return 100;
  }

  if (value >= 10) {
    return 10;
  }
  return 1;
}

/**
 * 计算数组数据brand_health值，如果是真没有数据，返回null，否则就是0
 * @param list
 * @returns
 */
export function dealwithArrayBrandHealth(list: Array<any> = []) {
  const item = list.find(item => item.key === 'brand_health');
  if (!item) return list;

  item.value = dealwithBrandHealth(item.value || -99999);
  return [...list.filter(item => item.key !== 'brand_health'), item];
}

/**
 * 对象中brand_health属性值，如果是-99999，将对象改为null，否则就是0
 * 数值型:brand_health，如果是-99999，返回null，否则就是0
 * @param list
 * @returns
 */
export function dealwithBrandHealth<
  T extends { [key in string]?: number | null } | number,
>(list: T): T {
  if (typeof list === 'number') {
    return (list === -99999 ? null : list) as T;
  }

  const result = list || ({} as T);

  // 使用类型保护确保 result 是一个对象
  if (
    typeof result === 'object' &&
    result !== null &&
    'brand_health' in result
  ) {
    if (result['brand_health'] === -99999) {
      result['brand_health'] = null;
    }
  }
  return result;
}

/**
 * 格式化百分比的Y轴
 * @param value
 * @returns
 */
export function eChartPercentageYAxis(value: number, digit: number = 0) {
  if (value === 0) return '0%';
  return `${(value * 100).toFixed(digit)}%`;
}

export function isNilOrEmpty(value: any): boolean {
  return (
    value === null || value === undefined || value === '' || Number.isNaN(value)
  );
}

export function getDefaultValue(value: any, type?: string | 'brand_health') {
  return isNilOrEmpty(value) ? (type === 'brand_health' ? -99999 : 0) : value;
}

/**
 * 计算topic情感分
 * 计算逻辑： 循环topics获取sentiment分数相加，然后除以topics长度
 * > 3.5 返回5
 * >= 2.5 返回3
 * 返回 1
 * @param topics 需要计算的topic
 * @param sentimentMap 以topic为key的对象，属性为sentiment是情感分
 * @returns 话题情感分数值
 */
export function calculateTopicSentiment(
  topics: Array<string>,
  sentimentMap: { [key: string]: { sentiment: number } },
): string {
  if (!topics || !topics.length || !sentimentMap) return sentimentTypeMap[1];
  const sum = topics.reduce(
    (acc, topic) => acc + (sentimentMap[topic]?.sentiment || 0),
    0,
  );
  const avg = sum / topics.length;

  if (avg > 3.5) return sentimentTypeMap[5];
  if (avg >= 2.5) return sentimentTypeMap[3];
  return sentimentTypeMap[1];
}
export function getSentimentIcon(sentiment: number) {
  if (sentiment > 3.5) {
    return 'happy-icon';
  }
  if (sentiment >= 2.5) {
    return 'expressionless-icon';
  }
  return 'unhappy-icon';
}

export function pickSupportedFilters(filter: any, supportKeys: string[]) {
  return Object.fromEntries(
    Object.entries(filter).filter(([key]) => supportKeys.includes(key)),
  );
}

export function dealwithAbsValue(value: number) {
  if (!value) return 0;
  if (value < 0) return 0;
  return value;
}
